import { useLiveQuery } from "dexie-react-hooks";
import type { ColaOp, HuayruroDB } from "../lib/db-local";
import { solesCent } from "../lib/money";

// D11 (Kevin, 30-sep): cuando el servidor rechaza una venta que estaba guardada en este equipo, la
// vendedora solo veía «N venta(s) rechazada(s) — revisar» y ninguna pantalla decía cuál. Esta lista se
// abre desde ese mismo aviso: no es una pantalla nueva (la cuenta de pantallas por rol no cambia) y no
// hace nada más que mostrar. Son las ventas de ESTE equipo: viven en su cola, no en el servidor.

export type LineaRechazada = { hora: string; items: { nombre: string; detalle: string }[]; total: string | null; motivo: string };

type ItemPayload = { producto_id?: string; cantidad?: number };

// Arma lo que se muestra de cada venta rechazada. Las ventas encoladas antes de D11 no traen resumen:
// para ellas se buscan los nombres en la copia del catálogo y el total queda sin decir.
export function armarLineas(ops: ColaOp[], nombrePorId: Map<string, string>): LineaRechazada[] {
  return ops
    .filter((op) => op.tipo === "venta" && op.estado === "rechazada")
    .sort((a, b) => a.creado_at.localeCompare(b.creado_at))
    .map((op) => {
      const hora = new Date(op.creado_at).toLocaleString("es-PE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
      const motivo = (op.ultimo_error ?? "sin motivo anotado").replace(/^[a-z_]+:\s*/, "");
      if (op.resumen) {
        return {
          hora,
          items: op.resumen.items.map((it) => ({ nombre: it.nombre, detalle: `${it.cantidad} × ${it.presentacion}` })),
          total: solesCent(op.resumen.total_cent),
          motivo,
        };
      }
      const items = (((op.payload as { items?: ItemPayload[] } | null)?.items ?? []) as ItemPayload[]).map((it) => ({
        nombre: (it.producto_id && nombrePorId.get(it.producto_id)) || "Producto que ya no está en este equipo",
        detalle: `${it.cantidad ?? "?"} unidad(es) de su presentación`,
      }));
      return { hora, items, total: null, motivo };
    });
}

export function VentasRechazadasModal({ db, onCerrar }: { db: HuayruroDB; onCerrar: () => void }) {
  const lineas = useLiveQuery(async () => {
    const ops = await db.cola_ops.where("estado").equals("rechazada").toArray();
    const ids = ops.flatMap((op) => ((op.payload as { items?: ItemPayload[] } | null)?.items ?? []).map((it) => it.producto_id ?? ""));
    const productos = await db.productos.bulkGet([...new Set(ids.filter(Boolean))]);
    const nombrePorId = new Map(productos.filter((p) => !!p).map((p) => [p!.id, p!.nombre]));
    return armarLineas(ops, nombrePorId);
  }, [db]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-surface/40 p-4 backdrop-blur" onClick={onCerrar}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rechazadas-titulo"
        className="flex max-h-[85dvh] w-full max-w-md flex-col rounded-[14px] border border-line bg-card shadow-[0_10px_30px_rgba(36,29,26,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-none px-5 pb-3 pt-5">
          <h2 id="rechazadas-titulo" className="text-xl font-bold text-ink">
            Ventas rechazadas en este equipo
          </h2>
          <p className="mt-1 text-[13px] text-ink-2">
            El sistema NO las registró. Si ya cobraste, avísale al Encargado con esta lista.
          </p>
        </div>
        <ul className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5" data-testid="lista-rechazadas">
          {(lineas ?? []).map((l, i) => (
            <li key={i} className="rounded-[10px] border border-line px-3 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-semibold text-ink">{l.hora}</span>
                <span className="text-[14px] font-bold text-ink">{l.total ?? "Total no guardado"}</span>
              </div>
              <ul className="mt-1 space-y-0.5">
                {l.items.map((it, j) => (
                  <li key={j} className="flex min-w-0 justify-between gap-3 text-[13px] text-ink-2">
                    {/* Sin cortar: con dos presentaciones parecidas, el nombre entero es lo que dice cuál fue. */}
                    <span className="min-w-0 break-words">{it.nombre}</span>
                    <span className="flex-none whitespace-nowrap">{it.detalle}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[12.5px] font-medium text-accent-ink">Motivo: {l.motivo}</p>
            </li>
          ))}
          {lineas && lineas.length === 0 && <li className="text-[13px] text-ink-2">No hay ventas rechazadas en este equipo.</li>}
        </ul>
        <div className="flex flex-none justify-end px-5 pb-5 pt-3">
          <button type="button" onClick={onCerrar} className="min-h-[44px] rounded-[9px] px-4 text-[13.5px] font-semibold text-ink-emph hover:bg-hover-btn">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
